import clsx from 'clsx';
import { isNull, toNumber } from 'lodash';
import { bool, number, object, string } from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icon } from 'components';

const FormInputCount = ({
  name,
  rules = {},
  label = '',
  labelHidden = false,
  placeholder = '',
  disabled = false,
  readonly = false,
  required = false,
  className = '',
  min = 0,
  max = null
}) => {
  const { t } = useTranslation();
  const { register, setValue, watch } = useFormContext();

  const quantity = watch(name);

  const disabledButtons = {
    plus: isNull(max) ? false : toNumber(quantity) >= max,
    minus: toNumber(quantity) <= min
  };

  const onChangeCount = (increment) => {
    if (disabled || (increment && disabledButtons.plus) || (!increment && disabledButtons.minus)) {
      return;
    }

    const newCount = toNumber(quantity) + (increment ? 1 : -1);
    setValue(name, newCount, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  };

  return (
    <div className={clsx('flex flex-col gap-y-1', className)}>
      <span className={clsx('text-xs input-count-label', labelHidden && 'sr-only')}>{label}</span>

      <div className="flex items-center">
        <button
          type="button"
          className="btn action text-gray-600"
          disabled={disabledButtons.minus}
          onClick={() => onChangeCount(false)}
        >
          <span className="sr-only">{t('buttons.remove')}</span>
          <Icon name="minus" />
        </button>

        <label>
          <span className="sr-only">{label}</span>

          <input
            className="text-center text-gray-600 w-10"
            name={name}
            type="number"
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readonly}
            required={required}
            min={min}
            max={max}
            {...(register && register(name, rules))}
          />
        </label>

        <button
          type="button"
          className="btn action text-gray-600"
          disabled={disabledButtons.plus}
          onClick={() => onChangeCount(true)}
          data-test-id="plusButton"
        >
          <span className="sr-only">{t('buttons.add')}</span>
          <Icon name="plus" />
        </button>
      </div>
    </div>
  );
};

FormInputCount.propTypes = {
  name: string.isRequired,
  rules: object,
  label: string,
  labelHidden: bool,
  placeholder: string,
  disabled: bool,
  readonly: bool,
  required: bool,
  className: string,
  min: number,
  max: number
};

export default FormInputCount;
